import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavBar } from 'antd-mobile';
import { getNavBarContainerStyle } from '../../../utils/deviceUtils';
import { t } from '../../../i18n';
import { DeliveringPath } from '../../../enums/DeliveryNavigatePathEnum';
import AccessCodeFormView from './components/AccessCodeFormView';
import { AccessCode } from './components/AccessInfoDisplay';
import {WEB_TEMPORARY_GRAY_ENABLED} from '../../../utils/grayscaleDriverUtils';

type FormMode = 'add' | 'update';

const AccessCodeFormPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // 从路由路径判断是新增还是更新模式
    const isUpdateMode = location.pathname.includes('/update-access-code');
    const mode: FormMode = isUpdateMode ? 'update' : 'add';

    // 从路由state获取参数
    const code = location.state?.code as AccessCode | undefined;
    const addressId = location.state?.addressId;
    const trackingNumber = location.state?.trackingNumber;
    const address = location.state?.displayAddress ?? location.state?.address;
    const addressLat = location.state?.addressLat as number | undefined;
    const addressLon = location.state?.addressLon as number | undefined;
    const orderId = location.state?.orderId as number | undefined;
    const fromMapMode = location.state?.fromMapMode as boolean | undefined;
    const fromCodeList = location.state?.fromCodeList as boolean | undefined; // 是否从 codeList 页面进入
    const skipDistanceLimit = location.state?.skipDistanceLimit as boolean | undefined;

    const handleBack = () => {
        // 如果来自地图模式，返回时导航到地图模式页面
        if (fromMapMode) {
            sessionStorage.setItem('returningFromDetail', 'true');

            const webTempGrayEnabled = localStorage.getItem(WEB_TEMPORARY_GRAY_ENABLED);

            if (webTempGrayEnabled) {
                // 灰度模式，跳转到临时地图页面
                navigate("/delivery/temporary/map-mode");
            } else {
                // 正常模式，跳转到正式地图页面
                navigate(DeliveringPath.DELIVERY_MAP_MODE);
            }
        } else {
            navigate(-1);
        }
    };

    // 更新模式下，如果没有code信息，显示错误
    if (mode === 'update' && !code) {
        return (
            <div className="flex flex-col h-screen bg-gray-50">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>
                        {t('pages.delivery.orderDetail.updateAccessCodeTitle')}
                    </NavBar>
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-gray-500">
                        {t('pages.delivery.orderDetail.codeNotFound')}
                    </div>
                </div>
            </div>
        );
    }

    // 如果没有orderId，显示错误
    if (!orderId) {
        return (
            <div className="flex flex-col h-screen bg-gray-50">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>
                        {mode === 'add' 
                            ? t('pages.delivery.orderDetail.addAccessCode')
                            : t('pages.delivery.orderDetail.updateAccessCodeTitle')
                        }
                    </NavBar>
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-gray-500">
                        {t('pages.delivery.orderDetail.orderIdRequired')}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen bg-gray-50">
            {/* Navbar */}
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleBack}>
                    {mode === 'add' 
                        ? t('pages.delivery.orderDetail.addAccessCode')
                        : t('pages.delivery.orderDetail.updateAccessCodeTitle')
                    }
                </NavBar>
            </div>

            {/* Form Content - 使用共享的 AccessCodeFormView 组件 */}
            <AccessCodeFormView
                mode={mode}
                code={code}
                addressId={addressId}
                trackingNumber={trackingNumber}
                address={address}
                addressLat={addressLat}
                addressLon={addressLon}
                orderId={orderId}
                onBack={handleBack}
                fromCodeList={fromCodeList}
                skipDistanceLimit={skipDistanceLimit}
            />
        </div>
    );
};

export default AccessCodeFormPage;

